export type BuildingId =
  | 'tradehub'
  | 'forge'
  | 'scrap_yard'
  | 'signal_beacon'
  | 'armory'
  | 'shield_pylon'
  | 'radar_tower'
  | 'loot_enhancer'
  | 'credit_generator';

export interface BuildingDef {
  id: BuildingId;
  name: string;
  description: string;
  footprintW: number;     // tiles wide
  footprintH: number;     // tiles tall
  maxLevel: number;
  costs: number[];        // credit cost per level [lv1, lv2, lv3]
  /** One-line effect description per level */
  effectDescriptions: string[];
  /** Hex color for grid rendering */
  color: number;
  /** Icon character for display */
  icon: string;
}

// ─── BUILDING DEFINITIONS ─────────────────────────────────

const TRADEHUB: BuildingDef = {
  id: 'tradehub',
  name: 'Tradehub',
  description: 'A merchant outpost. Unlocks the Trader.',
  footprintW: 3, footprintH: 3,
  maxLevel: 1,
  costs: [500],
  effectDescriptions: [
    'Unlocks the Trader',
  ],
  color: 0x6688aa,
  icon: '⚖',
};

const FORGE: BuildingDef = {
  id: 'forge',
  name: 'Forge',
  description: 'Combine same-type items into higher-tier equipment.',
  footprintW: 3, footprintH: 3,
  maxLevel: 3,
  costs: [800, 2400, 6000],
  effectDescriptions: [
    'Merge 3 same-tier items → next tier (basic quality)',
    'Merge ratio improved to 2:1',
    'Merged item keeps best input quality',
  ],
  color: 0xff6622,
  icon: '⚒',
};

const SCRAP_YARD: BuildingDef = {
  id: 'scrap_yard',
  name: 'Scrap Yard',
  description: 'Improves credits received when selling items.',
  footprintW: 2, footprintH: 2,
  maxLevel: 3,
  costs: [400, 1200, 3500],
  effectDescriptions: [
    'Sell price +10%',
    'Sell price +20%',
    'Sell price +30%',
  ],
  color: 0x888844,
  icon: '♻',
};

const SIGNAL_BEACON: BuildingDef = {
  id: 'signal_beacon',
  name: 'Signal Beacon',
  description: 'Boosts credits earned from exploration kills.',
  footprintW: 2, footprintH: 3,
  maxLevel: 3,
  costs: [1000, 3000, 8000],
  effectDescriptions: [
    'Kill credits +15%',
    'Kill credits +30%',
    'Kill credits +50%',
  ],
  color: 0x44aaff,
  icon: '📡',
};

const ARMORY: BuildingDef = {
  id: 'armory',
  name: 'Armory',
  description: 'Increases weapon damage for all equipped weapons.',
  footprintW: 3, footprintH: 2,
  maxLevel: 3,
  costs: [1200, 3600, 9000],
  effectDescriptions: [
    'Weapon damage +5%',
    'Weapon damage +10%',
    'Weapon damage +15%',
  ],
  color: 0xcc3333,
  icon: '⚔',
};

const SHIELD_PYLON: BuildingDef = {
  id: 'shield_pylon',
  name: 'Shield Pylon',
  description: 'Provides additional damage reduction to the robot.',
  footprintW: 2, footprintH: 2,
  maxLevel: 3,
  costs: [1000, 2800, 7000],
  effectDescriptions: [
    'Damage reduction +3%',
    'Damage reduction +6%',
    'Damage reduction +10%',
  ],
  color: 0x4488cc,
  icon: '🛡',
};

const RADAR_TOWER: BuildingDef = {
  id: 'radar_tower',
  name: 'Radar Tower',
  description: 'Extends robot detection and vision range.',
  footprintW: 2, footprintH: 4,
  maxLevel: 3,
  costs: [1400, 4000, 10000],
  effectDescriptions: [
    'Detection +1, Vision +0.5',
    'Detection +2, Vision +1',
    'Detection +3, Vision +1.5',
  ],
  color: 0x33cc66,
  icon: '📡',
};

const LOOT_ENHANCER: BuildingDef = {
  id: 'loot_enhancer',
  name: 'Loot Enhancer',
  description: 'Increases chance of finding higher-quality loot.',
  footprintW: 3, footprintH: 3,
  maxLevel: 3,
  costs: [2000, 6000, 16000],
  effectDescriptions: [
    'Quality bump chance +5%',
    'Quality bump chance +10%',
    'Quality bump chance +15%',
  ],
  color: 0xcc44cc,
  icon: '✦',
};

const CREDIT_GENERATOR: BuildingDef = {
  id: 'credit_generator',
  name: 'Credit Generator',
  description: 'Generates passive credits over time.',
  footprintW: 4, footprintH: 4,
  maxLevel: 3,
  costs: [3200, 10000, 25000],
  effectDescriptions: [
    '+2 credits/min',
    '+5 credits/min',
    '+10 credits/min',
  ],
  color: 0xccaa22,
  icon: '⚡',
};

// ─── ALL BUILDINGS ────────────────────────────────────────

export const ALL_BUILDINGS: BuildingDef[] = [
  TRADEHUB,
  FORGE,
  SCRAP_YARD,
  SIGNAL_BEACON,
  ARMORY,
  SHIELD_PYLON,
  RADAR_TOWER,
  LOOT_ENHANCER,
  CREDIT_GENERATOR,
];

export function getBuildingById(id: BuildingId): BuildingDef | undefined {
  return ALL_BUILDINGS.find(b => b.id === id);
}
